// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { z } from "zod";

import { fullAddressSchema } from "../lib/address/types";
import { DayOfTheWeek } from "../lib/dates/types";
import { WorkplaceNoteIdentifier } from "../redesign/Workplace/constants";

export const facilityPhotoSchema = z.object({
  _id: z.string(),
  index: z.string(),
  name: z.string(),
  fileStorageFileKey: z.string().optional(),
  fileStorageRootFolder: z.string().optional(),
  url: z.string().optional(),
});

export type FacilityPhoto = z.infer<typeof facilityPhotoSchema>;

export const facilityRatingsResponseSchema = z.object({
  total: z.number(),
  avg: z.number(),
});

export type FacilityRatings = z.infer<typeof facilityRatingsResponseSchema>;

const mandatoryBreakConfigurationSchema = z.object({
  breakDurationInMinutes: z.number(),
});

export type MandatoryBreakConfiguration = z.infer<typeof mandatoryBreakConfigurationSchema>;

const windowingStrategyConfigurationSchema = z.object({
  name: z.string(),
  config: z.record(z.string(), z.unknown()),
});

const windowingSequenceSchema = z
  .object({
    sequence: z.array(z.union([z.string(), windowingStrategyConfigurationSchema])),
  })
  .optional();

export type WindowingStrategyConfiguration = z.infer<typeof windowingStrategyConfigurationSchema>;

const chartingRemindersConfigSchema = z.object({
  bookedShiftDetails: z.discriminatedUnion("enabled", [
    z.object({
      enabled: z.literal(true),
    }),
    z.object({
      enabled: z.literal(false),
    }),
  ]),
  clockIn: z.discriminatedUnion("enabled", [
    z.object({
      enabled: z.literal(true),
    }),
    z.object({
      enabled: z.literal(false),
    }),
  ]),
  clockOut: z.discriminatedUnion("enabled", [
    z.object({
      enabled: z.literal(true),
    }),
    z.object({
      enabled: z.literal(false),
    }),
  ]),
  inProgressShiftDetails: z.discriminatedUnion("enabled", [
    z.object({
      enabled: z.literal(true),
    }),
    z.object({
      enabled: z.literal(false),
    }),
  ]),
});

const credentialSharingConfigurationSchema = z.object({
  chartingReminders: chartingRemindersConfigSchema,
});

export const facilityProfileResponseSchema = z.object({
  _id: z.string(),
  userId: z.string(),
  name: z.string(),
  type: z.string().optional(),
  requiresLunchBreak: z.boolean().nullish(),
  requiresLunchBreakSince: z.string().nullish(),
  pricingMsa: z.string().nullish(),
  providesRestBreaks: z.boolean().nullish(),
  providesRestBreaksSince: z.string().nullish(),
  instantBook: z.string().optional(),
  chartingCredentialSharingIsEnabled: z.boolean().optional(),
  credentialSharingConfiguration: credentialSharingConfigurationSchema.optional(),
  mandatoryBreakConfiguration: mandatoryBreakConfigurationSchema,
  windowingSequence: windowingSequenceSchema,
});

export type FacilityProfile = z.infer<typeof facilityProfileResponseSchema>;

export enum VerificationContactMethod {
  PHONE = "PHONE",
  EMAIL = "EMAIL",
  TEXT = "TEXT",
}
export enum FacilityVerificationMethod {
  TIMESHEET = "Timesheet Only",
  OUTBOUND = "Outbound Verification",
  SHEET = "External Google Sheet",
  OTHER = "Other",
}

const verificationContactSchema = z.object({
  archived: z.boolean(),
  contactMethod: z.object({
    name: z.nativeEnum(VerificationContactMethod).optional(),
  }),
  availability: z
    .array(
      z.object({
        day: z.nativeEnum(DayOfTheWeek),
      })
    )
    .optional(),
});

export const facilityVerificationPreferencesSchema = z.object({
  name: z.string().optional(),
  maxOutreachAttemptsPerDay: z.number().optional(),
  usesTimesheets: z.boolean().optional(),
  verificationMethod: z
    .object({
      name: z.nativeEnum(FacilityVerificationMethod),
    })
    .optional(),
  contacts: z.array(verificationContactSchema).optional(),
  outboundContactList: z.array(verificationContactSchema).optional(),
  preferenceType: z.string().optional(),
  verificationAttempt: z.number().optional(),
  requiresLunchBreak: z.boolean().optional(),
  mongodbId: z.string().optional(),
});
export type FacilityVerificationPreferences = z.infer<typeof facilityVerificationPreferencesSchema>;

export const facilityVerificationPreferencesResponseSchema = z.object({
  data: z.array(facilityVerificationPreferencesSchema),
  success: z.boolean(),
});
export type FacilityVerificationPreferencesResponse = z.infer<
  typeof facilityVerificationPreferencesResponseSchema
>;
export const authorizedSignatorySchema = z.object({
  _id: z.string(),
  name: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  role: z.string().nullish(),
});

export const facilityRuleSchema = z.object({
  _id: z.string(),
  text: z.string(),
  quizQuestionId: z.string().optional(),
  isOutdated: z.boolean().optional(),
});

export type FacilityRule = z.infer<typeof facilityRuleSchema>;

export const baseFacilityNoteSchema = z.object({
  category: z.string(),
  categoryId: z.string(),
  description: z.string(),
  hideInput: z.boolean(),
  sortOrder: z.number(),
  note: z.string().nullish(),
  noteId: z.string().nullish(),
  identifier: z.string().nullish(),
  updatedAt: z.string().nullish(),
  updatedBy: z.string().nullish(),
  isVisibleToWorker: z.boolean(),
  categoryTextVisibleToWorker: z.string(),
  rules: z.array(facilityRuleSchema).optional(),
});

type BaseFacilityNote = z.infer<typeof baseFacilityNoteSchema>;

export const mandatoryBreakPolicyNote = baseFacilityNoteSchema.extend({
  note: z.string(),
  noteId: z.string(),
  identifier: z.literal(WorkplaceNoteIdentifier.MANDATORY_BREAK_POLICY),
  updatedAt: z.string(),
  updatedBy: z.string(),
});

export type MandatoryBreakPolicyNote = z.infer<typeof mandatoryBreakPolicyNote>;

export function isMandatoryBreakPolicyNote(value: any): value is MandatoryBreakPolicyNote {
  return value?.identifier === WorkplaceNoteIdentifier.MANDATORY_BREAK_POLICY;
}

export const restBreakPolicyNote = baseFacilityNoteSchema.extend({
  note: z.string(),
  noteId: z.string(),
  identifier: z.literal(WorkplaceNoteIdentifier.REST_BREAK_POLICY),
  updatedAt: z.string(),
  updatedBy: z.string(),
});

export type RestBreakPolicyNote = z.infer<typeof restBreakPolicyNote>;

export function isRestBreakPolicyNote(value: any): value is RestBreakPolicyNote {
  return value?.identifier === WorkplaceNoteIdentifier.REST_BREAK_POLICY;
}

export const facilityNoteSchema = z.union([mandatoryBreakPolicyNote, baseFacilityNoteSchema]);

export type FacilityNote = BaseFacilityNote;

export const facilityNoteAcknowledgementSchema = z.object({
  category: z.string(),
  identifier: z.union([z.literal(WorkplaceNoteIdentifier.MANDATORY_BREAK_POLICY), z.string()]),
  note: z.string(),
  updatedAt: z.string(),
  noteId: z.string(),
});

export type FacilityNoteAcknowledgement = z.infer<typeof facilityNoteAcknowledgementSchema>;

export const facilitySchema = z.object({
  name: z.string(),
  userId: z.string().optional(),
  geoLocation: z.object({
    coordinates: z.tuple([z.number(), z.number()]),
    type: z.string(),
  }),
  verificationPreference: facilityVerificationPreferencesSchema.optional(),
  state: z.string().optional(),
  tmz: z.string().optional(),
  /**
   * @deprecated You are probably looking for `userId` and not this.
   * This is just a MongoDB's auto-generated internal ID which we don't regard as the facility ID in our system
   */
  _id: z.string().optional(),
  email: z.string().optional(),
  facilityId: z.string().optional(),
  phone: z.string().optional(),
  requiredDocuments: z.array(z.string()).optional(),
  fullAddress: fullAddressSchema.optional(),
  authorizedSignatories: z.array(authorizedSignatorySchema).optional(),
  requireTimecardPhoto: z.boolean().optional(),
  rates: z.record(z.string(), z.number()).optional(),
  shiftConfirmationInstructions: z.string().optional(),
  type: z.string().optional(),
  lateCancellation: z
    .object({
      period: z.number(),
      feeHours: z.number(),
    })
    .optional(),
  sentHomeChargeHours: z.number().optional(),
  featureSettings: z
    .object({
      // Note: These are incomplete and loosely typed, need to be updated once
      // the backend provides a well typed response.
      /**
       * @deprecated This field is no longer used and will be removed in the future.
       */
      radarTimekeepingValidations: z.boolean().optional(),
      displayTimeSheetSummary: z.boolean().optional(),
      digitalSignature: z.boolean().optional(),
      californiaTimesheet: z.boolean().optional(),
      uploadTimesheetNonIP: z.boolean().optional(),
      isMissedPunchRequestEnabled: z.boolean().optional(),
    })
    .optional(),
  distance: z.number().optional(),
  isApproxDistance: z.boolean().optional().optional(),
  checkInInstructions: z.string().optional(),
  requiresLunchBreak: z.boolean().optional(),
  requiresLunchBreakSince: z.string().nullish(),
  providesRestBreaks: z.boolean().optional(),
  providesRestBreaksSince: z.string().nullish(),
  preventDoubleShifts: z.boolean().optional(),
  maxAllowedWorkHoursPerWeek: z.number().nullish(),
  maxAllowedWorkConsecutiveHours: z.number().nullish(),
  quizWorkersOnRules: z.boolean().nullish(),
  mandatoryBreakConfiguration: mandatoryBreakConfigurationSchema.optional(),
  restrictOverworkShiftInvites: z.boolean().optional(),
});

export type Facility = z.infer<typeof facilitySchema>;

export const shiftSlotSchema = z.object({
  workplaceUserId: z.string(),
  stats: z.record(z.string(), z.object({ assignedShifts: z.number(), totalShifts: z.number() })),
  start: z.string(),
  end: z.string(),
  bookedBy: z.array(
    z.object({
      name: z.string(),
      userId: z.string(),
      qualification: z.string(),
      hasProfilePic: z.boolean(),
    })
  ),
});
